import { WhDatePicker } from 'src/ui/DatePicker/WhDatePicker';

/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useMemo } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Space, Form, Select } from 'src/antd';
import { T } from '@transifex/react';
import { useParams } from 'react-router-dom';
import TextArea from 'antd/es/input/TextArea';
import useMediaQuery from 'use-media-antd-query/es/useMediaQuery';
import { WasteFractionTag } from 'src/ui';
import { VALIDATION_MESSAGE } from '../../../../../../../../utils/validation';
// eslint-disable-next-line import/no-cycle
import SelectableContainersList from '../../../../../../../../new-components/SelectableContainersList';
import OrderOverview from '../../../../../../../shared/OrderOverview';
import { PropertyContext } from '../../../../../../../../context/property-context';
// eslint-disable-next-line import/no-cycle
import { useGetPropertyContainers } from '../../../../../../../../api/property/property-containers';
import { ChangeFromTo, PrefferedDate } from '../../elements/Summary';
import { PreferredDateStyles } from '../../styles';
import { StopPropagationDiv } from '../../../../../../../shared/StopPropagationDiv';
import { createMarkUp } from '../../../../../../../../utils/createMarkup';
// eslint-disable-next-line import/no-cycle
import { useTicketAvailableDates } from '../useTicketAvailableDates';
import { DATE_FORMAT } from '../consts';
import CustomFieldsFormSection from '../CustomFieldsFormSection';

const Content = () => {
  const { ticketTypes, id: propertyId } = useContext(PropertyContext);
  const { ticketTypeId } = useParams<{ ticketTypeId: string }>();

  const availableDates = useTicketAvailableDates({
    propertyId,
    ticketTypeId,
  });

  const ticketType: $TSFixMe = ticketTypes?.filter(
    (e: { id: string }) => e.id === ticketTypeId
  );

  const {
    portalDateHidden,
    portalDateLabel,
    portalDateHelpText,
    portalDateRequired,
    portalDescription,
    portalNoteHidden,
    portalNoteLabel,
    portalNoteHelpText,
    portalNoteRequired,

    // eslint-disable-next-line no-unsafe-optional-chaining
  } = ticketType?.[0];

  const isMobile = useMediaQuery('(max-width: 996px)');
  return (
    <>
      {portalDescription && (
        <div
          style={{ marginBottom: '1rem' }}
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={createMarkUp(portalDescription)}
        />
      )}
      <Form.Item
        label={portalNoteLabel || <T _str="Note" />}
        name={['manageContainers', 'note']}
        tooltip={portalNoteHelpText || undefined}
        rules={[
          {
            required: portalNoteRequired,
            message: VALIDATION_MESSAGE.REQUIRED,
          },
        ]}
        hidden={portalNoteHidden}
      >
        <TextArea style={{ marginBottom: '1rem' }} />
      </Form.Item>
      <SelectableContainersList
        isPickupSchemeChange
        preName={['manageContainers', 'items']}
        renderItem={({ id, data }, checked) => {
          const {
            container: {
              waste_fraction: wasteFraction,
              pickup_setting: { name: currentTitle, id: currentValue },
              container_type: { name: containerType },
            },
            prices,
          } = data;
          return {
            title: !isMobile ? (
              <>
                <WasteFractionTag wasteFraction={wasteFraction} iconOnly />
                {wasteFraction.name}
                {' - '}
                {containerType}
              </>
            ) : (
              <div style={{ textAlign: 'start' }}>
                {wasteFraction}
                <br />
                {containerType}
              </div>
            ),
            addonCenter: <T _str="Current: {value}" value={currentTitle} />,
            addonEnd: (
              <StopPropagationDiv>
                <Form.Item
                  name={['manageContainers', 'items', id, 'pickupSetting']}
                  dependencies={[['manageContainers', 'items', id, 'checked']]}
                  rules={[
                    {
                      required: checked,
                      message: VALIDATION_MESSAGE.REQUIRED,
                    },
                  ]}
                >
                  <Select
                    placeholder={<T _str="Pickup setting" />}
                    style={{ width: '200px' }}
                    disabled={!checked}
                    options={prices.change_pickup_setting
                      .map((node: $TSFixMe) => ({
                        label: node.pickup_setting.name,
                        value: node.pickup_setting.id,
                      }))
                      .filter(({ value }: $TSFixMe) => value !== currentValue)}
                  />
                </Form.Item>
              </StopPropagationDiv>
            ),
            addonContent: !portalDateHidden ? (
              <StopPropagationDiv>
                <PreferredDateStyles>
                  <Form.Item
                    label={portalDateLabel || <T _str="Preferred date" />}
                    name={['manageContainers', 'items', id, 'preferredDate']}
                    tooltip={portalDateHelpText || undefined}
                    rules={[
                      {
                        required: portalDateRequired && checked,
                        message: VALIDATION_MESSAGE.REQUIRED,
                      },
                    ]}
                    hidden={portalDateHidden}
                  >
                    <WhDatePicker
                      format={DATE_FORMAT}
                      inputReadOnly
                      disabled={!checked}
                      disabledDate={(date) =>
                        !(availableDates.get(id) || [])?.includes(
                          date.format('YYYY-MM-DD')
                        )
                      }
                    />
                  </Form.Item>
                </PreferredDateStyles>
              </StopPropagationDiv>
            ) : undefined,
          };
        }}
      />
      <CustomFieldsFormSection />
    </>
  );
};
const BreadCrumbTitle = <T _str="Select containers" />;

const Summary = () => {
  const { id: propertyId, ticketTypesMap } = useContext(PropertyContext);
  const { data } = useGetPropertyContainers(propertyId, {
    includePrices: true,
  });
  const { ticketTypeId } = useParams<{
    ticketTypeId: string;
  }>();
  const ticketType = useMemo(
    () => ticketTypesMap?.[ticketTypeId],
    [ticketTypeId, ticketTypesMap]
  );

  const returnContainerById = (id: string) => {
    return data?.data?.filter(
      (container: $TSFixMe) => container?.container?.id === id
    )?.[0];
  };

  const values = Form.useWatch(['manageContainers']);

  const items = Object.keys(values?.items || {})
    .map((containerId) => ({
      ...values?.items?.[containerId],
      id: containerId,
    }))
    .filter((n) => n.checked)
    .map(({ id, pickupSetting, containerType, preferredDate }) => ({
      containerId: id,
      pickupSettingId: pickupSetting,
      containerTypeId: containerType,
      preferredDate,
    }));

  const { portalDateLabel, portalDateHidden } = ticketType;

  // #TODO Get container waste fraction - get previous pickup setting
  const summaryItems = items?.map((item: $TSFixMe) => {
    const container = returnContainerById(item?.containerId);

    const newPickupSetting = container?.prices?.change_pickup_setting?.filter(
      (price: { pickup_setting: { id: $TSFixMe } }) =>
        price.pickup_setting.id === item.pickupSettingId
    )?.[0];

    const oldPickupSetting = container?.prices?.change_pickup_setting?.filter(
      (price: { pickup_setting: { id: $TSFixMe } }) =>
        price.pickup_setting.id === container?.container?.pickup_setting?.id
    )?.[0];

    return {
      icon: <DeleteOutlined />,
      header: container?.container?.waste_fraction?.name,
      headerSuffix: container?.container?.container_id,
      description: (
        <Space direction="vertical">
          <ChangeFromTo
            from={oldPickupSetting?.pickup_setting?.name}
            to={newPickupSetting?.pickup_setting?.name}
          />
          <PrefferedDate
            value={item.preferredDate}
            dateText={portalDateLabel}
            dateHidden={portalDateHidden}
          />
        </Space>
      ),
      price:
        newPickupSetting?.price?.container_price > 0
          ? // eslint-disable-next-line no-unsafe-optional-chaining
            newPickupSetting?.price?.container_price / 100
          : 0,
      previousPrice:
        oldPickupSetting?.price?.container_price > 0
          ? // eslint-disable-next-line no-unsafe-optional-chaining
            oldPickupSetting?.price?.container_price / 100
          : 0,
      recurringPrice: true,
    };
  });

  return <OrderOverview header="" items={summaryItems} />;
};

export const FormChangePickupSetting = {
  Form: { BreadCrumbTitle, Content },
  Summary,
};
